<template>
  <div>
    <!--   Wirkleistung   -->
    <parameter-block
      :showToggle="true"
      :content="[
        {
          label: $t('Web.ExpertMode.Sections.Battery'),
          value: Math.round((latestPowerInverterReadApp.parameters || {}).Inverter_Bat_Power),
          unit: 'W',
          hideFractionDigits: true
        },
        {
          label: $t('Web.ExpertMode.Items.Power.Grid'),
          value: Math.round((latestGridMeterApp.parameters || {}).Meter_Grid_Power_Sum),
          unit: 'W',
          hideFractionDigits: true
        },
        {
          label: $t('Web.ExpertMode.Items.Power.PV'),
          value: Math.round((latestPvMeterApp.parameters || {}).Meter_PV_Power_Sum),
          unit: 'W',
          hideFractionDigits: true
        },
        {
          label: $t('Web.ExpertMode.Items.Power.Consumer'),
          value: Math.round(consumedPower),
          unit: 'W',
          hideFractionDigits: true
        }
      ]"
    >
      {{ $t('Web.ExpertMode.Sections.Power') }}
    </parameter-block>

    <!--   Energy Data   -->
    <parameter-block
      v-if="isTechnician"
      :showToggle="true"
      :content="[
        {
          label: $t('Web.expert_view_total_power_battery'),
          value: (latestPowerInverterReadApp.parameters || {}).Inverter_Energy_Day / 1000,
          unit: 'kWh'
        },
        {
          label: $t('Web.expert_view_total_power_grid_feed'),
          value: (latestGridMeterApp.parameters || {}).Meter_Grid_Energy_Export_T1 / 1000,
          unit: 'kWh'
        },
        {
          label: $t('Web.expert_view_total_power_grid_reference'),
          value: (latestGridMeterApp.parameters || {}).Meter_Grid_Energy_Import_T1 / 1000,
          unit: 'kWh'
        },
        {
          label: $t('Web.expert_view_total_power_solar_feed'),
          value: (latestPvMeterApp.parameters || {}).Meter_PV_Energy_Import_T1 / 1000,
          unit: 'kWh'
        }
      ]"
    >
      {{ $t('Web.expert_view_total_power_title') }}
    </parameter-block>

    <!--   Inverter   -->
    <parameter-block
      v-if="isTechnician"
      :showToggle="true"
      :content="[
        {
          label: $t('Web.expert_view_battery_inverter_performance_discharge_set_point'),
          value: (latestPowerInverterReadApp.parameters || {}).Inverter_Pac_set_UpperLimit_W || 0,
          unit: 'W',
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_battery_inverter_performance_charge_set_point'),
          value: (latestPowerInverterReadApp.parameters || {}).Inverter_Pac_set_LowerLimit_W || 0,
          unit: 'W',
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_battery_inverter_charging_power'),
          value:
            (latestPowerInverterReadApp.parameters || {}).Inverter_Pac_charge_max_LowerLimit_W ||
            'n/a',
          unit: 'W',
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_battery_inverter_discharging'),
          value:
            (latestPowerInverterReadApp.parameters || {}).Inverter_Pac_discharge_max_UpperLimit_W ||
            0,
          unit: 'W',
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_battery_inverter_voltage_max'),
          value:
            (latestPowerInverterReadApp.parameters || {}).Inverter_U_bat_set_max_UpperLimit_mV /
              1000 || 0,
          unit: 'V',
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_battery_inverter_voltage_min'),
          value:
            (latestPowerInverterReadApp.parameters || {}).Inverter_U_bat_set_min_LowerLimit_mV /
              1000 || 0,
          unit: 'V',
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_battery_inverter_charging_current_max'),
          value:
            (latestPowerInverterReadApp.parameters || {}).Inverter_I_bat_charge_max_LowerLimit_mA /
              1000 || 0,
          unit: 'A'
        },
        {
          label: $t('Web.expert_view_battery_inverter_discharging_current_max'),
          value:
            (latestPowerInverterReadApp.parameters || {})
              .Inverter_I_bat_discharge_max_UpperLimit_mA / 1000 || 0,
          unit: 'A'
        },
        {
          label: $t('Web.expert_view_battery_inverter_cos_current'),
          value: cosPhiLatest
        },
        {
          label: $t('Web.expert_view_battery_inverter_powerfactor'),
          value: (latestPowerInverterReadApp.parameters || {}).PowerFactor || 0,
          unit: '%'
        },
        {
          label: $t('Web.expert_view_battery_inverter_q_fix_var'),
          value: (latestPowerInverterReadApp.parameters || {}).Q_fix_var || 0,
          unit: 'var'
        },
        {
          label: $t('Web.expert_view_battery_inverter_q_cr_time'),
          value: (latestPowerInverterReadApp.parameters || {}).Q_CR_Time || 0,
          unit: 's'
        },
        {
          label: $t('Web.expert_view_battery_inverter_qmax_smax'),
          value: (latestPowerInverterReadApp.parameters || {}).QMax_SMax || 0,
          unit: '%'
        },
        {
          label: $t('Web.ProductionData'),
          value: ((latestPowerInverterReadApp.parameters || {}).ProductionData || 0).toString(),
          ruleSubscriber: 'ar4105Monitoring',
          target: 'hidden'
        }
      ]"
    >
      {{ $t('Web.ExpertMode.Items.Power.Inverter') }}
    </parameter-block>

    <!--   Battery Data   -->
    <parameter-block
      v-if="isTechnician"
      :showToggle="true"
      :content="[
        {
          label: $t('Web.ExpertMode.Items.Battery.MaxCharge'),
          value: (latestPower3ParameterPublisherApp.parameters || {}).LG_Battery_CH_LIMIT / 1000,
          unit: 'A'
        },
        {
          label: $t('Web.expert_view_battery_inverter_discharging_current'),
          value: (latestPower3ParameterPublisherApp.parameters || {}).LG_Battery_DCH_LIMIT / 1000,
          unit: 'A'
        },
        {
          label: $t('Web.ExpertMode.Items.Battery.Battery'),
          value: (latestLgBatteryApp.parameters || {}).LG_Battery_SOC || 0,
          unit: '%',
          hideFractionDigits: true
        },
        {
          label: $t('Web.ExpertMode.Items.Battery.Cycles'),
          value: (latestLgBatteryApp.parameters || {}).Battery_Module1_Cycles || 0,
          hideFractionDigits: true
        },
        {
          label: $t('Web.ExpertMode.Items.Battery.Voltage'),
          value: (latestLgBatteryApp.parameters || {}).Battery_Voltage || 0,
          unit: 'V',
          hideFractionDigits: true
        }
      ]"
    >
      {{ $t('Web.Items.Battery.Title') }}
    </parameter-block>

    <!--   Error codes   -->
    <parameter-block
      :showToggle="true"
      :content="[
        {
          label: $t('Web.expert_view_error_inverter'),
          value: (latestPower3ParameterPublisherApp.parameters || {}).System_SM_Inverter_Error,
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_error_battery'),
          value: (latestPower3ParameterPublisherApp.parameters || {}).System_SM_Battery_Error,
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_error_system'),
          value: (latestPower3ParameterPublisherApp.parameters || {}).System_SM_Status,
          hideFractionDigits: true
        }
      ]"
    >
      {{ $t('Web.expert_view_error_title') }}
    </parameter-block>

    <!--  Versions    -->
    <version-data :device="device" :apiVersion="apiVersion" />

    <!-- Smart Battery inverter versions -->
    <parameter-block
      :showToggle="true"
      :content="[
        {
          label: $t('Web.expert_view_version_inverter_ens'),
          value: (latestPowerInverterReadApp.parameters || {}).ENS,
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_version_inverter_pu'),
          value: (latestPowerInverterReadApp.parameters || {}).PU,
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_version_inverter_bdc'),
          value: (latestPowerInverterReadApp.parameters || {}).BDC,
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_version_inverter_hmi_app'),
          value: (latestPowerInverterReadApp.parameters || {}).HMI_APP,
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_version_inverter_hmi_fbl'),
          value: (latestPowerInverterReadApp.parameters || {}).HMI_FBL,
          hideFractionDigits: true
        }
      ]"
    >
      {{ $t('Web.ExpertMode.Items.Inverter.title') }}
    </parameter-block>

    <!-- TOR -->
    <parameter-block
      :showToggle="true"
      :content="[
        {
          label: $t('Web.expert_view_cr_time_read'),
          value: (latestPowerInverterReadApp.parameters || {}).P_U_CRTime,
          unit: 's',
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_recon_time_read'),
          value: (latestPowerInverterReadApp.parameters || {}).Recon_Time / 1000,
          unit: 's',
          hideFractionDigits: true
        },

        {
          label: $t('Web.expert_view_voltage_dropout_hi'),
          value: (latestPowerInverterReadApp.parameters || {}).U_UpperRR / 10,
          unit: '%',
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_voltage_dropout_lo'),
          value: (latestPowerInverterReadApp.parameters || {}).U_LowerRR / 10,
          unit: '%',
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_u1_limit_hi'),
          value: (latestPowerInverterReadApp.parameters || {}).U_1UpLimit / 10,
          unit: '%',
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_u2_limit_lo'),
          value: (latestPowerInverterReadApp.parameters || {}).U_2LoLimit,
          unit: '%',
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_u1_time_hi'),
          value: (latestPowerInverterReadApp.parameters || {}).U_1UpTime / 1000,
          unit: 's',
          hideFractionDigits: false
        },
        {
          label: $t('Web.expert_view_u2_time_lo'),
          value: (latestPowerInverterReadApp.parameters || {}).U_2LoTime / 1000,
          unit: 's',
          hideFractionDigits: false
        },
        {
          label: $t('Web.expert_view_f1_limit_hi'),
          value: (latestPowerInverterReadApp.parameters || {}).F_1UpLimit / 1000,
          unit: 'Hz',
          hideFractionDigits: false
        },
        {
          label: $t('Web.expert_view_f1_limit_lo'),
          value: (latestPowerInverterReadApp.parameters || {}).F_1LoLimit / 1000,
          unit: 'Hz',
          hideFractionDigits: false
        },
        {
          label: $t('Web.expert_view_u1_limit_lo'),
          value: (latestPowerInverterReadApp.parameters || {}).U_1LoLimit / 10,
          unit: '%',
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_u1_time_lo'),
          value: (latestPowerInverterReadApp.parameters || {}).U_1LoTime / 1000,
          unit: 's',
          hideFractionDigits: false
        },
        {
          label: $t('Web.expert_view_u_ave_hi'),
          value: (latestPowerInverterReadApp.parameters || {}).U_UpAve / 10,
          unit: '%',
          hideFractionDigits: true
        },
        {
          label: $t('Web.expert_view_f_derate'),
          value: (latestPowerInverterReadApp.parameters || {}).F_Derate / 1000,
          unit: 'Hz',
          hideFractionDigits: false
        },
        {
          label: $t('Web.expert_view_f_grad_reduc'),
          value: calcFGradeReducValue((latestPowerInverterReadApp.parameters || {}).F_GradReduc),
          unit: '%',
          hideFractionDigits: true
        }
      ]"
      v-if="getAustriaSelected"
    >
      {{ $t('Web.expert_view_tor_read_title') }}
    </parameter-block>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import ParameterBlock from '../_common/parameterBlock';
import VersionData from '../_common/versionData';

import UtilMixin from '@/helpers/mixins';

export default {
  name: 'ReadOnlyValues',
  components: { ParameterBlock, VersionData },
  mixins: [UtilMixin],
  computed: {
    ...mapGetters([
      'apiVersion',
      'device',
      'isTechnician',
      'latestGridMeterApp',
      'latestLgBatteryApp',
      'latestPvMeterApp',
      'latestPower3ParameterPublisherApp',
      'latestPowerInverterReadApp',
      'getExpertviewParams'
    ]),

    cosPhiLatest() {
      /* Cos Phi calculation:

      documentation from "S T E C A":

      values < 1000 -> cap.
      values > 1000 -> ind.

      value = 1000 -> Cos Phi = 1
      value = 900 -> Cos Phi = 0,9 cap
      value = 1100 -> Cos Phi = 0,9 ind

      factor = 1000

      example:

      0,93 cap (send as 930)
      or
      0,93 ind (send as 1030)

      base (= 1000) |     distance from base + scale

      1f            -   (Math.abs(value - 1000) / 1000f) = 0.93 */
      const val = (this.latestPowerInverterReadApp.parameters || {}).Inverter_Read_CosPhi;
      const base = 1000;

      if (val === base) {
        return this.getCosPhi(val);
      } else if (val > base) {
        return this.getCosPhi(val) + ' ind.';
      } else if (val < base) {
        return this.getCosPhi(val) + ' cap.';
      } else {
        return 'n/a';
      }
    },

    consumedPower() {
      const gridPowerSum = (this.latestGridMeterApp.parameters || {}).Meter_Grid_Power_Sum || 0;
      const inverterBatPower =
        (this.latestPowerInverterReadApp.parameters || {}).Inverter_Bat_Power || 0;
      const pvPowerSum = (this.latestPvMeterApp.parameters || {}).Meter_PV_Power_Sum || 0;
      return pvPowerSum + inverterBatPower + gridPowerSum;
    },

    getAustriaSelected() {
      return this.$store.state.expertviewRuleProvider.austriaSelected;
    }
  },
  methods: {
    calcFGradeReducValue(value) {
      // Calc F_GRAD_REDUC value => (1 / value) * 0.02 * 100000
      let fGradReducValue = (1 / value) * 0.02 * 100000;
      return fGradReducValue;
    }
  },
  watch: {
    getAustriaSelected(newCount) {
      return newCount;
    }
  }
};
</script>

<style lang="scss" scoped>
.parameterBlock {
  margin-top: 30px;
}
</style>
